// in/cm toggle for a size chart, rendered by snippets/size-chart.liquid.
//
// A custom element rather than a DOMContentLoaded script: the Size Guide lives in a
// <template> that content-drawer.js clones on every open, so a page-load
// querySelectorAll would never see these nodes. connectedCallback runs per clone.
//
// This does no arithmetic. Liquid computes the centimetre value for every
// measurement cell and leaves both on the element as data-size-chart-in /
// data-size-chart-cm, so switching units is a textContent swap. Which columns count
// as measurements is decided in Liquid too — these charts mix real measurements with
// numeric size labels ("Numeric (THT)" runs 46/48/50), and only the header tells them
// apart, so a cell carrying no cm attribute is deliberately left alone here.

const UNIT_IN = "in";
const UNIT_CM = "cm";

// The shopper's choice, shared by every chart on the page rather than held per
// instance. A product can have several charts behind tabs, and reading inches on one
// tab and centimetres on the next would be its own small bug. Module scope also means
// the choice survives the drawer being closed and reopened, since each open clones a
// fresh element that reads this on connect.
let activeUnit = UNIT_IN;

const SYNC_EVENT = "size-chart:units";

class SizeChartUnits extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.onSync = this.onSync.bind(this);

    this.addEventListener("click", this.onClick);
    document.addEventListener(SYNC_EVENT, this.onSync);

    // Adopt whatever was last chosen, so a chart cloned into a reopened drawer or
    // revealed by a tab does not snap back to inches.
    this.apply(activeUnit);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    document.removeEventListener(SYNC_EVENT, this.onSync);
  }

  onClick(event) {
    const button = event.target.closest("[data-size-chart-unit]");
    if (!button) return;

    const unit = button.dataset.sizeChartUnit === UNIT_CM ? UNIT_CM : UNIT_IN;
    if (unit === activeUnit) return;

    activeUnit = unit;
    // Tell the other charts, then paint this one. Dispatching on document rather than
    // walking the DOM keeps this working wherever the charts happen to sit.
    document.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: { unit } }));
  }

  onSync(event) {
    this.apply(event.detail?.unit === UNIT_CM ? UNIT_CM : UNIT_IN);
  }

  apply(unit) {
    const showCm = unit === UNIT_CM;

    for (const cell of this.querySelectorAll("[data-size-chart-cm]")) {
      const { sizeChartIn, sizeChartCm } = cell.dataset;
      cell.textContent = showCm ? sizeChartCm : sizeChartIn;
    }

    for (const button of this.querySelectorAll("[data-size-chart-unit]")) {
      button.setAttribute("aria-pressed", String(button.dataset.sizeChartUnit === unit));
    }

    const caption = this.querySelector("[data-size-chart-caption]");
    if (caption) {
      const text = showCm ? caption.dataset.captionCm : caption.dataset.captionIn;
      if (text) caption.textContent = text;
    }
  }
}

if (!customElements.get("size-chart-units")) {
  customElements.define("size-chart-units", SizeChartUnits);
}
